// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

//
// HW.12 (COB-332) — node-safe device primitives for the verifier-spa's
// NATIVE hardware ownership/ack arm.
//
// This file is a *narrow subset* of user-app's key-agent device stack
// (`web/key-agent/src/crypto.ts` device helpers + `ledger-agent.ts`),
// COPIED not imported per the arm's-length ground rule (D4). It holds only
// what the verifier surface needs to prove ownership and complete the
// contract-ack ceremony on a Ledger:
//   - the device key layout (per-vault U account path + leaf), byte-identical
//     to user-app's so the verifier derives to the same on-chain pubkey the
//     vault was created with;
//   - the `bsm-bip137-v1` proof shape + parse/verify (WS-H spec §2/§4), the
//     browser twin of the verifier's Rust `app_signature`;
//   - the Ledger-app → network inference that drives the coin type.
//
// DELIBERATELY node-safe: it imports NO device-transport deps
// (`ledger-bitcoin` / `@ledgerhq/hw-transport-webhid`). The actual WebHID
// I/O lives in verifier-spa's lazy `hardware-connect.ts`, so this module —
// and every unit test — evaluates without a `navigator.hid`.
//
// The verifier NEVER co-signs PSBTs in the browser (its server does), so this
// stack is permanently limited to message signing: none of user-app's PSBT /
// policy-registration / xpub-export surface is ported, and never will be.
//

import { secp256k1 } from '@noble/curves/secp256k1';
import { sha256 } from '@noble/hashes/sha256';
import { concatBytes, hexToBytes } from '@noble/hashes/utils';

// ── Device key layout ──────────────────────────────────────────────
//
// Byte-identical to user-app's `crypto.ts` (HW.1/HW.2). The vault's user
// multisig key (descriptor U slot) lives at
//   m/84'/{coin}'/{2k+2}'/0/0
// where k is the per-vault counter the verifier stores as
// `user_derivation_index` and hands the SPA on the existence lookup, and
// {coin} is 0' on mainnet / 1' on every test network. The `2k+2` mapping
// (never account 0'/1', which are the user's own + personal wallet) and the
// `0/0` leaf MUST match user-app exactly — the whole premise is that the
// device reproduces the same pubkey the vault descriptor commits to.

export type NetworkName = 'mainnet' | 'testnet' | 'signet' | 'regtest';

/** BIP-84 coin type for a network: mainnet `0'`, every test network `1'`. */
export function deviceCoinType(network: NetworkName): 0 | 1 {
  return network === 'mainnet' ? 0 : 1;
}

/** Leaf suffix (relative to the account node) of the device U multisig key. */
export const DEVICE_LEAF_PATH = '0/0';

/** Highest vault-pair counter k such that account 2k+3 ≤ 100 stays a
 *  Ledger-standard (silently exportable) account index. Matches user-app. */
export const MAX_DEVICE_VAULT_COUNTER = 48;

function assertDeviceAccount(deviceAccount: number): void {
  if (!Number.isInteger(deviceAccount) || deviceAccount < 0) {
    throw new Error(
      `Device account must be a non-negative integer, got ${deviceAccount}`,
    );
  }
  if (deviceAccount > MAX_DEVICE_VAULT_COUNTER) {
    throw new Error(
      `Device vault-account space exhausted: counter ${deviceAccount} exceeds ` +
        `${MAX_DEVICE_VAULT_COUNTER}`,
    );
  }
}

/** Device account path for a vault's U key: `m/84'/{coin}'/{2k+2}'`. */
export function deviceMultisigAccountPath(
  network: NetworkName,
  deviceAccount: number,
): string {
  assertDeviceAccount(deviceAccount);
  return `m/84'/${deviceCoinType(network)}'/${2 * deviceAccount + 2}'`;
}

/** Full device path of the vault's U multisig signing key — the account node
 *  plus the `0/0` leaf. This is the exact path the Ledger BSM-signs the
 *  ownership + contract-ack messages at, and the key the verifier re-derives
 *  from its stored descriptor. */
export function deviceVaultMultisigMessagePath(
  network: NetworkName,
  deviceAccount: number,
): string {
  return `${deviceMultisigAccountPath(network, deviceAccount)}/${DEVICE_LEAF_PATH}`;
}

// ── Ledger-app → network inference ─────────────────────────────────
//
// The verifier surface has no per-vault network to hand the SPA before the
// ownership gate (the existence lookup is deliberately minimal — it does not
// leak the deposit address or its network). Instead we infer the coin type
// from the Ledger's OPEN APP: the device that holds the key is the
// authoritative source, and the app the user opened is the network their vault
// lives on. A mismatch (mainnet app for a signet vault) derives the wrong key
// and fails closed at `/prove-v2` verify — a recoverable, clearly-messaged
// error rather than a silent wrong-key sign.

/** Thrown when the open Ledger app is neither Bitcoin nor Bitcoin Test. */
export class WrongDeviceAppError extends Error {
  readonly appName: string;

  constructor(appName: string) {
    super(
      `The Ledger has the "${appName}" app open. Open the "Bitcoin" app for a ` +
        `mainnet vault, or the "Bitcoin Test" app for a signet / testnet vault ` +
        `(install via Ledger Live → My Ledger if missing), then reconnect.`,
    );
    this.name = 'WrongDeviceAppError';
    this.appName = appName;
  }
}

/** Ledger app name expected for a network: coin `1'` paths live in "Bitcoin
 *  Test". The inverse of {@link networkForLedgerApp}. */
export function expectedDeviceApp(network: NetworkName): string {
  return network === 'mainnet' ? 'Bitcoin' : 'Bitcoin Test';
}

/**
 * Infer the vault network from the Ledger's open app. `Bitcoin` → mainnet
 * (coin `0'`); `Bitcoin Test` → signet (coin `1'`, the label for the whole
 * tb1 test family — signet / testnet / regtest all share coin `1'`). Any
 * other app (the dashboard, Ethereum, …) throws {@link WrongDeviceAppError}.
 */
export function networkForLedgerApp(appName: string): NetworkName {
  if (appName === 'Bitcoin') return 'mainnet';
  if (appName === 'Bitcoin Test') return 'signet';
  throw new WrongDeviceAppError(appName);
}

// ── WS-H proof object (spec §2) ────────────────────────────────────

/** A versioned app-signature proof (WS-H spec §2). The verifier's `/prove-v2`
 *  and `/ack-v2` accept both variants; a Ledger only ever emits
 *  `bsm-bip137-v1`. Byte-identical discriminated union to user-app's. */
export type AppSignatureProof =
  | { format: 'der-sha256-v1'; signature_der_hex: string }
  | { format: 'bsm-bip137-v1'; signature_base64: string };

/** Serialized-proof size cap in bytes (WS-H spec §7.5). */
const MAX_PROOF_BYTES = 1024;
/** Challenge-message size cap in bytes (WS-H spec §7.5). */
const MAX_MESSAGE_BYTES = 4096;

/**
 * Validate the shape of a BIP-137 compact recoverable signature as base64 —
 * what the device's `signMessage` must have returned before we wrap it into a
 * `bsm-bip137-v1` proof: exactly 88 padded standard base64 chars decoding to
 * 65 bytes, header in `27..=42`, low-S (WS-H spec §4). Fail-closed client-side
 * belt-and-braces; the verifier re-enforces every rule. Returns the decoded
 * bytes. Copied verbatim from user-app's `crypto.ts`.
 */
export function assertBsmSignatureShape(signatureBase64: string): Uint8Array {
  if (signatureBase64.length !== 88) {
    throw new Error('device signature must be 88 base64 chars');
  }
  let raw: string;
  try {
    raw = atob(signatureBase64);
  } catch {
    throw new Error('device signature is not valid base64');
  }
  if (raw.length !== 65) {
    throw new Error('device signature must decode to 65 bytes');
  }
  const bytes = Uint8Array.from(raw, (c) => c.charCodeAt(0));
  // Strict encoding: round-trip must reproduce the input exactly (atob is
  // WHATWG-forgiving about padding; the verifier is not).
  if (btoa(raw) !== signatureBase64) {
    throw new Error('device signature is not canonical base64');
  }
  const header = bytes[0];
  if (header < 27 || header > 42) {
    throw new Error(`device signature header byte ${header} out of BIP-137 range`);
  }
  // Low-S: s is the last 32 bytes; reject s > n/2 (the verifier rejects too —
  // catching it here turns a would-be 401 into a typed error at the device seam).
  let s = 0n;
  for (let i = 33; i < 65; i++) s = (s << 8n) | BigInt(bytes[i]);
  if (s > secp256k1.CURVE.n >> 1n) {
    throw new Error('device signature is not low-S');
  }
  return bytes;
}

/** Wrap a device BSM base64 signature into a `bsm-bip137-v1` proof, after
 *  shape-checking it (88→65B, header 27..=42, low-S). The one place a raw
 *  device signature becomes a proof object the native arm posts. */
export function bsmProofFromBase64(signatureBase64: string): AppSignatureProof {
  assertBsmSignatureShape(signatureBase64);
  return { format: 'bsm-bip137-v1', signature_base64: signatureBase64 };
}

/** Constant-length byte-equality (exact compressed-SEC1 key compare). */
function bytesEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a[i] ^ b[i];
  return diff === 0;
}

/**
 * Bitcoin Signed Message digest (WS-H spec §4): the double-SHA-256 of
 * `varstr("Bitcoin Signed Message:\n") || varstr(message)`. Messages here are
 * always < 64 KiB (the §7.5 cap is 4096), so the compact-size length prefix is
 * one byte (< 253) or the three-byte `0xfd`-prefixed little-endian form.
 * Copied verbatim from user-app's `crypto.ts`.
 */
function bsmDigest(message: string): Uint8Array {
  const msg = new TextEncoder().encode(message);
  const prefix = new TextEncoder().encode('Bitcoin Signed Message:\n');
  const len =
    msg.length < 253
      ? Uint8Array.of(msg.length)
      : Uint8Array.of(0xfd, msg.length & 0xff, (msg.length >> 8) & 0xff);
  const payload = concatBytes(Uint8Array.of(prefix.length), prefix, len, msg);
  return sha256(sha256(payload));
}

/**
 * Parse + validate a WS-H proof object (spec §2) from an untyped value — the
 * browser twin of the Rust `app_signature::parse_proof`. Strict: exact keys
 * per variant, a known `format` only (unknown or the reserved
 * `bip322-simple-p2wpkh-v2` reject), within the serialized size cap. Throws on
 * any violation; the format is never sniffed from signature shape (spec §8).
 * Copied verbatim from user-app's `crypto.ts`.
 */
export function parseAppSignatureProof(value: unknown): AppSignatureProof {
  if (JSON.stringify(value).length > MAX_PROOF_BYTES) {
    throw new Error('proof exceeds size cap');
  }
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new Error('proof must be a JSON object');
  }
  const obj = value as Record<string, unknown>;
  const keyCount = Object.keys(obj).length;
  switch (obj.format) {
    case 'der-sha256-v1':
      if (keyCount !== 2 || typeof obj.signature_der_hex !== 'string') {
        throw new Error('malformed der-sha256-v1 proof');
      }
      return { format: 'der-sha256-v1', signature_der_hex: obj.signature_der_hex };
    case 'bsm-bip137-v1':
      if (keyCount !== 2 || typeof obj.signature_base64 !== 'string') {
        throw new Error('malformed bsm-bip137-v1 proof');
      }
      return { format: 'bsm-bip137-v1', signature_base64: obj.signature_base64 };
    default:
      throw new Error('unknown or reserved proof format');
  }
}

/**
 * Verify a WS-H proof object over `message` against `expectedPubkeyHex` — the
 * browser twin of the Rust `app_signature::verify_proof`. Key binding is an
 * exact compressed-SEC1 byte compare; low-S is mandatory in both formats
 * (reject, never normalize). Fails closed: any malformed input, out-of-range
 * value, or verification failure returns `false`, never throws. Copied verbatim
 * from user-app's `crypto.ts`; used by tests to assert a device proof recovers
 * to the vault's user multisig key.
 */
export function verifyAppSignatureProof(
  message: string,
  expectedPubkeyHex: string,
  proof: AppSignatureProof,
): boolean {
  try {
    if (new TextEncoder().encode(message).length > MAX_MESSAGE_BYTES) return false;
    if (!/^0[23][0-9a-f]{64}$/.test(expectedPubkeyHex)) return false;
    const expected = hexToBytes(expectedPubkeyHex);
    if (proof.format === 'der-sha256-v1') {
      const hex = proof.signature_der_hex;
      // Strict lowercase DER, 8..=72 bytes (16..=144 hex chars).
      if (!/^[0-9a-f]+$/.test(hex) || hex.length < 16 || hex.length > 144) {
        return false;
      }
      const digest = sha256(new TextEncoder().encode(message));
      // lowS:true (noble default) rejects high-S — reject-not-normalize.
      return secp256k1.verify(hexToBytes(hex), digest, expected, { format: 'der' });
    }
    // bsm-bip137-v1: shape-check (88→65B, header 27..=42, low-S), recover, compare.
    const bytes = assertBsmSignatureShape(proof.signature_base64);
    const recid = (bytes[0] - 27) & 3;
    const sig = secp256k1.Signature.fromCompact(bytes.slice(1, 65)).addRecoveryBit(recid);
    const recovered = sig.recoverPublicKey(bsmDigest(message)).toRawBytes(true);
    return bytesEqual(recovered, expected);
  } catch {
    return false;
  }
}
